import React, { useMemo, useState } from "react";
import { Checkbox, Box, Typography, Button, TextField } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeItem2 } from "@mui/x-tree-view/TreeItem2";

function collectIds(node) {
  const out = [];
  const walk = (n) => {
    out.push(n.id);
    if (n.children) n.children.forEach(walk);
  };
  walk(node);
  return out;
}

const labelOf = (sap, name, names) => {
  const n = name || names.get(String(sap));
  return n && n.trim() ? `${sap} - ${n}` : `SAP ${sap}`;
};

function filterItems(list, q) {
  const out = [];
  list.forEach((n) => {
    const self = (n.label || "").toLowerCase().includes(q);
    const kids = n.children ? filterItems(n.children, q) : [];
    if (self) out.push(n);
    else if (kids.length) out.push({ ...n, children: kids });
  });
  return out;
}

function getDescendantValues(node) {
  const out = [];
  const walk = (n) => {
    if (n.value !== undefined) out.push(n.value);
    if (n.children) n.children.forEach(walk);
  };
  walk(node);
  return out;
}

export default function ProductTree({
  mappings = [],
  sets = [],
  productNames = new Map(),
  selected = [],
  onChange,
}) {
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    const roots = [];
    const mappingItems = mappings.map((m) => {
      const node = {
        id: `mm-${m.sap_id}`,
        label: labelOf(m.sap_id, m.name, productNames),
        value: m.sap_id,
      };
      if (m.maps_to === m.sap_id) return node;
      return {
        ...node,
        children: [
          {
            id: `mm-${m.sap_id}-target`,
            label: labelOf(m.maps_to, m.maps_to_name, productNames),
            value: m.maps_to,
          },
        ],
      };
    });
    if (mappingItems.length) {
      roots.push({ id: "mappings", label: "Product mappings", children: mappingItems });
    }
    const setItems = sets.map((s) => ({
      id: `ss-${s.name}`,
      label: s.name,
      children: (s.products || []).map((p) => ({
        id: `ss-${s.name}-${p.sap_id}`,
        label: labelOf(p.sap_id, p.name, productNames),
        value: p.sap_id,
      })),
    }));
    if (setItems.length) {
      roots.push({ id: "sets", label: "Product sets", children: setItems });
    }
    return roots;
  }, [mappings, sets, productNames]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q ? filterItems(items, q) : items), [items, q]);
  const expanded = useMemo(() => (q ? shown.flatMap(collectIds) : []), [shown, q]);

  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  const idToNode = useMemo(() => {
    const m = new Map();
    const walk = (nodes) => {
      (nodes || []).forEach((n) => {
        m.set(n.id, n);
        if (n.children) walk(n.children);
      });
    };
    walk(shown);
    return m;
  }, [shown]);

  function ProductItem({ id, itemId, label, children }) {
    const node = idToNode.get(itemId);
    if (!node) {
      return (
        <TreeItem2 id={id} itemId={itemId} label={label}>
          {children}
        </TreeItem2>
      );
    }
    const values = getDescendantValues(node);
    if (!values.length) {
      return (
        <TreeItem2 id={id} itemId={itemId} label={label}>
          {children}
        </TreeItem2>
      );
    }
    const checked = values.every((v) => selectedSet.has(v));
    const indeterminate = !checked && values.some((v) => selectedSet.has(v));

    const handleCheckbox = () => {
      const next = new Set(selectedSet);
      const any = values.some((v) => selectedSet.has(v));
      values.forEach((v) => (any ? next.delete(v) : next.add(v)));
      onChange?.([...next]);
    };

    return (
      <TreeItem2
        id={id}
        itemId={itemId}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={handleCheckbox}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
            <Typography variant="body2">{label}</Typography>
          </Box>
        }
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </TreeItem2>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
        <Typography variant="subtitle2">{selected.length} product(s) selected</Typography>
        {selected.length > 0 && (
          <Button size="small" onClick={() => onChange?.([])}>
            Clear
          </Button>
        )}
      </Box>
      <TextField
        size="small"
        placeholder="Type to filter products..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ width: 600, mb: 0.5 }}
      />
      {shown.length ? (
        <RichTreeView
          key={q || "__all__"}
          items={shown}
          defaultExpandedItems={expanded}
          slots={{ item: ProductItem, expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
          disableSelection
          sx={{ width: 600, maxHeight: 360, overflow: "auto" }}
        />
      ) : (
        <Typography variant="body2">No products match.</Typography>
      )}
    </Box>
  );
}
